/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Grid2 as Grid,
  TextField,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { validate } from "validate.js";
import {
  resetStateReports,
  submitReport,
  updateReport,
} from "../../app/slices/reportsSlice";
import AppMessage from "../../components/AppMessage/AppMessage";
import moment from "moment";
import { generateRandomString, getBase64 } from "../../app/utils";

const ReportDialog = ({ open, onClose, method, selectedReport }) => {
  const {
    data: dataPost,
    status: statusPost,
    error: errorPost,
  } = useSelector(({ reports }) => reports.post);
  const {
    data: dataPut,
    status: statusPut,
    error: errorPut,
  } = useSelector(({ reports }) => reports.put);

  const schema = {
    title: {
      presence: { allowEmpty: false },
    },
    description: {
      presence: { allowEmpty: false },
    },
  };

  const statuses = [
    { value: "A", label: "Active" },
    { value: "I", label: "Inactive" },
  ];

  const [formValues, setFormValues] = useState(
    method === "UPDATE"
      ? {
          status: selectedReport?.status,
          description: selectedReport?.description,
          title: selectedReport?.title,
        }
      : {}
  );
  const [errors, setErrors] = useState([]);
  const [response, setResponse] = useState(null);
  const dispatch = useDispatch();

  //use effect
  useEffect(() => {
    dispatch(resetStateReports(["post", "put"]));
    return () => {
      dispatch(resetStateReports(["post", "put"]));
    };
  }, []);

  useEffect(() => {
    if (errorPost || errorPut) {
      setResponse({
        type: "error",
        message:
          errorPost?.message || errorPut?.message || "Something went wrong",
      });
    }
  }, [errorPost, errorPut]);

  useEffect(() => {
    if (dataPost || dataPut) {
      setResponse({
        type: "success",
        message:
          dataPost !== null
            ? "Report posted successfully"
            : "Report updated successfully",
      });

      if (method === "ADD") {
        setFormValues({});
      }
    }
  }, [dataPost, dataPut]);

  //methods
  const handleSubmit = async () => {
    let _errors = validate(
      formValues,
      method === "ADD"
        ? {
            ...schema,
            attachment: {
              presence: { allowEmpty: false },
            },
          }
        : schema
    );

    if (_errors) {
      setErrors(_errors);
      return;
    } else {
      setErrors([]);
    }

    dispatch(resetStateReports(["post", "put"]));

    if (method === "ADD") {
      let _fileName = `REP-${moment().format(
        "DDMMYYYY"
      )}-${generateRandomString(4)}.${formValues?.attachment
        ?.split(".")
        .pop()}`;

      let base64 = await getBase64(formValues?.attachmentFile);

      let _payload = {
        title: formValues?.title,
        description: formValues?.description,
        attachment: _fileName,
        _meta: [
          { path: "../uploads/reports/", fileName: _fileName, file: base64 },
        ],
      };

      dispatch(submitReport(_payload));
    } else if (method === "UPDATE") {
      dispatch(
        updateReport({
          id: selectedReport?.report_id,
          payload: {
            title: formValues?.title,
            description: formValues?.description,
            status: formValues?.status,
          },
        })
      );
    }
  };

  const handleChange = (e) => {
    const inputName = e?.target?.name;
    const inputValue = e?.target?.value;

    setFormValues({
      ...formValues,
      [inputName]: inputValue,
    });
  };

  const handleFileChange = (e) => {
    setFormValues({
      ...formValues,
      attachment: e?.target?.files[0]?.name || "",
      attachmentFile: e?.target?.files[0],
    });
  };

  //builders
  const BuildForm = () => {
    return (
      <Grid container spacing={2}>
        <Grid size={{ md: 6, xs: 12 }}>
          <TextField
            fullWidth
            required
            variant="outlined"
            type="text"
            name="title"
            error={errors["title"] ? true : false}
            helperText={errors["title"] ? errors.title[0] : null}
            id="title"
            label="Report Title"
            onChange={handleChange}
            value={formValues?.title || ""}
          />
        </Grid>
        <Grid size={{ md: 6, xs: 12 }}>
          <TextField
            fullWidth
            required
            disabled={method === "UPDATE"}
            variant="outlined"
            slotProps={{
              inputLabel: { shrink: true },
              // htmlInput: { accept: "application/pdf" },
            }}
            type="file"
            name="attachment"
            id="attachment"
            label="Attachment"
            error={errors["attachment"] ? true : false}
            helperText={errors["attachment"] ? errors.attachment[0] : null}
            onChange={handleFileChange}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            required
            multiline
            rows={3}
            variant="outlined"
            type="text"
            name="description"
            id="description"
            label="Description"
            error={errors["description"] ? true : false}
            helperText={errors["description"] ? errors.description[0] : null}
            onChange={handleChange}
            value={formValues?.description || ""}
          />
        </Grid>

        {method === "UPDATE" && (
          <Grid item size={{ xs: 12, md: 6 }}>
            <TextField
              required
              fullWidth
              variant="outlined"
              name="status"
              id="incident-rating"
              select
              label="Status"
              error={errors["status"] ? true : false}
              helperText={errors["status"] ? errors.status[0] : null}
              onChange={handleChange}
              value={formValues?.status}
            >
              {statuses.map((option) => {
                return <MenuItem value={option.value}>{option.label}</MenuItem>;
              })}
            </TextField>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth={"md"}
      disableBackdropClick
      disableEscapeKeyDown
      open={open}
      aria-labelledby="responsive-dialog-title"
    >
      <DialogTitle id="responsive-dialog-title">
        <span id="modal-header">
          <b>Post Report</b>
        </span>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <div className="p-10">{BuildForm()}</div>
          {response && (
            <AppMessage
              isOpen={response !== null}
              message={response?.message}
              messageType={response?.type}
              onClose={() => {
                setResponse(null);
              }}
            />
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {dataPost === null && (
          <Button
            id="btn-confirm"
            variant="contained"
            size="small"
            disabled={statusPost === "loading"}
            onClick={handleSubmit}
            color="primary"
          >
            {statusPost === "loading" && (
              <CircularProgress
                size={20}
                color="white"
                style={{ marginRight: "5px" }}
              />
            )}
            {method === "UPDATE" ? "Update" : "Post"}
          </Button>
        )}

        <Button
          id="btn-close"
          variant="contained"
          size="small"
          onClick={() => {
            onClose(dataPost || dataPut ? true : false);
          }}
          color="error"
        >
          <CloseIcon fontSize="small" className="app-button-icon" /> Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportDialog;
